import * as React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import axios from "axios";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { BASE_URL } from "../../../config/config";

const rs = (n) => `Rs ${Number(n || 0).toLocaleString()}`;

// Shows a tenant exactly what leaving early will cost, BEFORE they commit to a
// date. Every figure comes from the termination rules the owner already
// configured in Property Settings plus the tenant's own unpaid bills — nothing
// here is entered by hand, and nothing is a surprise at settlement time.
export default function TerminationCostPanel({ flatId, moveOutDate }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!flatId) return;

    setLoading(true);
    setError("");

    const query = moveOutDate ? `?moveOutDate=${moveOutDate}` : "";

    axios
      .get(`${BASE_URL}/termination-preview/${flatId}${query}`)
      .then((res) => {
        if (res.data?.success) setData(res.data);
        else setError(res.data?.message || "Could not load settlement details");
      })
      .catch((err) =>
        setError(err.response?.data?.message || "Could not load settlement details")
      )
      .finally(() => setLoading(false));
  }, [flatId, moveOutDate]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  // Everything owed has to be cleared before the tenancy can end — say so
  // plainly, with the itemised list, even when there are no early-exit charges.
  const duesAlert =
    data?.outstandingDues > 0 ? (
      <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
        <strong>You owe {rs(data.outstandingDues)}.</strong> Your move-out will
        only complete once all bills, rent and late fees are cleared.
        <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
          {(data.dues || []).map((d) => (
            <li key={d.id}>
              {d.label} {d.cycleKey ? `(${d.cycleKey})` : ""} — {rs(d.payable)}
              {d.penalty > 0 ? ` (incl. late fee ${rs(d.penalty)})` : ""}
            </li>
          ))}
        </Box>
      </Alert>
    ) : null;

  if (!data?.preview) return duesAlert;

  const p = data.preview;

  return (
    <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: "1px solid #e5e7eb" }}>
      <Typography
        variant="subtitle1"
        fontWeight={800}
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <ReceiptLongIcon fontSize="small" color="primary" /> If you leave early
      </Typography>
      {duesAlert}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
        {p.hasRules
          ? `Based on the "${p.settingName}" terms applied to this property.`
          : p.note}
      </Typography>

      {p.notice && p.notice.required > 0 && (
        <Alert
          severity={p.notice.shortfallDays > 0 ? "warning" : "success"}
          variant="outlined"
          sx={{ mb: 2 }}
        >
          {p.notice.given == null
            ? `Your agreement requires ${p.notice.required} days' notice. Pick a move-out date to see the exact figure.`
            : p.notice.shortfallDays > 0
            ? `You're giving ${p.notice.given} days' notice but ${p.notice.required} are required — ${p.notice.shortfallDays} day${
                p.notice.shortfallDays === 1 ? "" : "s"
              } short. Moving out later reduces this charge.`
            : `You're giving ${p.notice.given} days' notice — that meets the ${p.notice.required}-day requirement.`}
        </Alert>
      )}

      {p.charges?.length > 0 && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 800, color: "#B91C1C" }}>
            YOU WILL BE CHARGED
          </Typography>
          <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
            {p.charges.map((c) => (
              <Box
                key={c.key}
                sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {c.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.detail}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={800} sx={{ whiteSpace: "nowrap" }}>
                  {rs(c.amount)}
                </Typography>
              </Box>
            ))}
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" fontWeight={800}>
                Total charges
              </Typography>
              <Typography variant="body2" fontWeight={800} color="error">
                {rs(p.totalCharges)}
              </Typography>
            </Box>
          </Stack>
        </>
      )}

      {p.refunds?.length > 0 && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 800, color: "#047857" }}>
            HELD BY OWNER
          </Typography>
          <Stack spacing={1} sx={{ mt: 1, mb: 2 }}>
            {p.refunds.map((r) => (
              <Box key={r.key}>
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {r.label} ({rs(r.held)})
                  </Typography>
                  <Typography variant="body2" fontWeight={800} color="success.main">
                    {rs(r.refund)} back
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {r.detail}
                  {r.deducted > 0 ? ` — ${rs(r.deducted)} deducted.` : ""}
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}

      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: p.netPayable > 0 ? "#FEF2F2" : "#ECFDF5",
          border: `1px solid ${p.netPayable > 0 ? "#FCA5A5" : "#6EE7B7"}`,
        }}
      >
        <Typography variant="body2" fontWeight={800}>
          {p.netPayable > 0
            ? `You would still owe ${rs(p.netPayable)} after deposits are applied.`
            : `You would get back ${rs(p.totalRefunds)}.`}
        </Typography>
        {p.refundWithin && (
          <Typography variant="caption" color="text.secondary">
            Refundable within {p.refundWithin} of move-out.
          </Typography>
        )}
      </Box>

      {p.conditions?.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" sx={{ fontWeight: 800 }}>
            BEFORE YOUR DEPOSIT IS RELEASED
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}>
            {p.conditions.map((c) => (
              <Chip key={c} size="small" label={c} variant="outlined" />
            ))}
          </Stack>
        </Box>
      )}

      {p.mutualTermination?.allowed && (
        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
          Your agreement allows <strong>mutual termination</strong>
          {p.mutualTermination.penalty > 0
            ? ` with a ${rs(p.mutualTermination.penalty)} penalty`
            : " with no penalty"}
          . If the owner agrees to end the tenancy by mutual consent, the
          early-exit charges above may not apply — worth asking first.
        </Alert>
      )}

      {p.disputeMethod && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: "block" }}>
          Disagree with any of this? Your agreement resolves disputes via{" "}
          <strong>{p.disputeMethod}</strong>.
        </Typography>
      )}
    </Paper>
  );
}
